import '@babylonjs/core/Lights/Shadows/shadowGeneratorSceneComponent';
import { adjustLightToBoundingSphere, createLight, waitFor, Viewer, ViewerEvents, createGroundForSceneShadows, BaseExtension, checkValueAsBool } from '@immersive-tech/viewer-engine';
import { LightEvents, ManifestHandlerEvents } from '@/viewerExtensions/events';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';

import type { IViewerMeshInitializedEvent, IDirectionalLightWithShadow, Nullable, IGLTFMetaData } from '@immersive-tech/viewer-engine';
import type { Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import type { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import type { IExtensionManifestLoadedEvent } from '@/types/events/IExtensionManifestLoadedEvent';
import type { IManifest } from '@/types/IManifest';

export class Lights extends BaseExtension {
  protected ground: Nullable<Mesh>;
  public lights: IDirectionalLightWithShadow[];
  public shadowLight: Nullable<IDirectionalLightWithShadow>;
  protected meshes: (Mesh | AbstractMesh)[];
  protected sceneJson: Nullable<IManifest> = null;

  constructor(viewer: Viewer) {
    super(viewer);

    this.ground = null;
    this.lights = [];
    this.shadowLight = null;
    this.meshes = [];

    this.viewer.addEventListener(ViewerEvents.MODEL_ADDED_TO_SCENE, this.modelAddedToSceneHandler.bind(this));
    this.viewer.addEventListener(ViewerEvents.MESH_INITIALIZED, this.meshInitializedHandler.bind(this) as EventListener);
    this.viewer.addEventListener(ViewerEvents.MODEL_DESTROYED, this.modelDestroyedHandler.bind(this));
    this.viewer.addEventListener(ManifestHandlerEvents.MANIFEST_LOADED, this.manifestLoadedHandler.bind(this) as EventListener);
  }

  public hideEntityShadow(entityIdentifier: string) {
    if (this.lights.length === 0) return;

    if (this.viewer.modelContainers.get(entityIdentifier)?.meshes) {
      for (const mesh of this.viewer.modelContainers.get(entityIdentifier)!.meshes) {
        this.shadowLight?.shadowGenerator?.removeShadowCaster(mesh, false);
      }
    }

    this.viewer.dispatchViewerEvent(LightEvents.SHADOWS_HIDDEN, { data: entityIdentifier });
  }

  public showEntityShadow(entityIdentifier: string) {
    if (this.lights.length === 0) return;

    if (this.viewer.modelContainers.get(entityIdentifier)?.meshes) {
      for (const mesh of this.viewer.modelContainers.get(entityIdentifier)!.meshes) {
        if (mesh.metadata?.gltf?.extras?.castShadow != 0 && mesh.material?.metadata?.gltf?.extras?.castShadow != 0) {
          this.shadowLight?.shadowGenerator?.addShadowCaster(mesh, false);
        }
      }
    }

    this.viewer.dispatchViewerEvent(LightEvents.SHADOWS_SHOWN, { data: entityIdentifier });
  }

  protected modelDestroyedHandler() {
    if (!this.viewer.scene) return;

    const sceneMeshes = this.viewer.scene.meshes;

    for (let index = 0; index < this.lights.length; ++index) {
      const mesh = this.meshes[index] as AbstractMesh;
      if (sceneMeshes.includes(mesh)) continue;

      mesh.dispose();
      delete this.meshes[index];
      this.meshes.splice(index, 1);
    }

    this.createGround();
  }

  protected meshInitializedHandler(event: CustomEvent<IViewerMeshInitializedEvent>) {
    const { data: mesh } = event.detail;
    const meshMetadata = mesh.metadata as Nullable<IGLTFMetaData>;
    const materialMetadata = mesh.material?.metadata as Nullable<IGLTFMetaData>;
    const isMeshCastShadowDefined = typeof meshMetadata?.gltf?.extras?.castShadow !== 'undefined';
    const isMaterialCastShadowDefined = typeof materialMetadata?.gltf?.extras?.castShadow !== 'undefined';
    let shouldCastShadow = true;

    // By default every mesh should cast a shadow, but we will also respect castShadow property from
    // the mesh, material, and the parent - prioritized in that order
    // If castShadow property is missing from any of the above, it will not affect shouldCastShadow
    if (isMeshCastShadowDefined) {
      shouldCastShadow = checkValueAsBool(meshMetadata.gltf?.extras?.castShadow);
    } else if (isMaterialCastShadowDefined) {
      shouldCastShadow = checkValueAsBool(materialMetadata.gltf?.extras?.castShadow);
    } else if (mesh.parent instanceof TransformNode) {
      const parentMeshMetadata = mesh.parent.metadata as Nullable<IGLTFMetaData>;
      const isParentCastShadowDefined = typeof parentMeshMetadata?.gltf?.extras?.castShadow !== 'undefined';

      if (isParentCastShadowDefined) {
        shouldCastShadow = checkValueAsBool(parentMeshMetadata.gltf?.extras?.castShadow);
      }
    }

    if (shouldCastShadow) {
      this.shadowLight?.shadowGenerator?.addShadowCaster(mesh, false);
    }
  }

  public adjustLightToBoundingSphere(light: IDirectionalLightWithShadow) {
    adjustLightToBoundingSphere(light, this.meshes);

    this.viewer.renderNextFrame += 1;
  }

  protected async modelAddedToSceneHandler() {
    for (const [, modelContainer] of this.viewer.modelContainers) {
      this.meshes.push(...modelContainer.meshes);
    }

    for (const light of this.lights) {
      this.adjustLightToBoundingSphere(light);
    }

    this.createGround();

    this.viewer.renderSemaphore += 1;
    await waitFor(150);
    this.viewer.renderSemaphore -= 1;
  }

  public createLight(direction: Vector3, lightIntensity: number, enableShadows = true) {
    if (!this.viewer.scene) return;

    const newLightIndex = this.lights.length;
    const newLight = createLight(this.viewer.scene, 'light' + newLightIndex, direction, lightIntensity, enableShadows);

    if (newLightIndex === 0) {
      this.shadowLight = newLight;
    }

    this.lights.push(newLight);
    this.viewer.entities.set('light' + newLightIndex, newLight);

    this.viewer.renderNextFrame += 1;

    this.viewer.dispatchViewerEvent(LightEvents.MANIFEST_LIGHTS_ADDED, { data: newLight });
  }

  public destroyLight(lightIndex: number) {
    this.lights[lightIndex].dispose();
    delete this.lights[lightIndex];
    this.lights.splice(lightIndex, 1);
    this.viewer.renderNextFrame += 1;

    this.viewer.dispatchViewerEvent(LightEvents.LIGHT_DESTROYED, { data: lightIndex });
  }

  public createGround() {
    if (!this.viewer.scene || !this.viewer.camera) return;

    this.destroyGround();

    if (this.sceneJson && this.sceneJson.hideFloor !== true) {
      this.ground = createGroundForSceneShadows(this.viewer.scene, this.viewer.camera) ?? null;
    }
  }

  public destroyGround() {
    if (!this.viewer.scene || !this.viewer.camera) return;

    this.ground?.material?.dispose();
    this.ground?.dispose();

    this.viewer.renderNextFrame += 1;
  }

  protected manifestLoadedHandler(event: CustomEvent<IExtensionManifestLoadedEvent>) {
    this.sceneJson = event.detail.data;
  }
}
